import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Inbox, AlertCircle, ExternalLink } from 'lucide-react';

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const mono = (v) => <span className="font-mono text-xs text-muted-foreground">{v || '—'}</span>;
const fmtPeriod = (p) => {
  if (!p) return '—';
  const m = /^(\d{4})-(\d{2})$/.exec(String(p));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : String(p);
};

const StatusPill = ({ value }) => {
  const v = (value || 'active').toLowerCase();
  const cls = v === 'active'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
    : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${cls}`}>{v}</span>;
};

const COLUMNS = {
  students: [
    { h: 'ID', r: (s) => mono(s.custom_id) },
    { h: 'Name', r: (s) => <span className="font-medium">{s.name}</span> },
    { h: 'Sport', r: (s) => s.sport || '—' },
    { h: 'Batch', r: (s) => s.batch || '—' },
    { h: 'Contact', r: (s) => s.phone || s.guardian_phone || '—' },
    { h: 'Status', r: (s) => <StatusPill value={s.status} /> },
    { h: 'Joined', r: (s) => fmtDate(s.created_at) },
  ],
  employees: [
    { h: 'ID', r: (e) => mono(e.custom_id) },
    { h: 'Name', r: (e) => <span className="font-medium">{e.name}</span> },
    { h: 'Designation', r: (e) => e.designation || e.role || '—' },
    { h: 'Department', r: (e) => e.department || '—' },
    { h: 'Contact', r: (e) => e.phone || e.email || '—' },
    { h: 'Status', r: (e) => <StatusPill value={e.status} /> },
    { h: 'Joined', r: (e) => fmtDate(e.join_date || e.created_at) },
  ],
  revenue: [
    { h: 'Receipt', r: (f) => mono(f.custom_id) },
    { h: 'Student', r: (f) => <span className="font-medium">{f.student_name || '—'}</span> },
    { h: 'Type', r: (f) => <span className="capitalize">{f.fee_type || '—'}</span> },
    { h: 'Period', r: (f) => fmtPeriod(f.period) },
    { h: 'Method', r: (f) => <span className="capitalize">{f.payment_method || '—'}</span> },
    { h: 'Paid On', r: (f) => fmtDate(f.payment_date) },
    { h: 'Amount', right: true, r: (f) => <span className="font-semibold">{inr(f.amount)}</span> },
  ],
  sports: [
    { h: 'Sport', r: (s) => <span className="font-medium">{s.sport}</span> },
    { h: 'Students', right: true, r: (s) => s.students },
    { h: 'Active', right: true, r: (s) => s.active_students },
    { h: 'Batches', right: true, r: (s) => s.batches },
    { h: 'Revenue', right: true, r: (s) => <span className="font-semibold">{inr(s.revenue)}</span> },
  ],
};

const TITLES = { students: 'Students', employees: 'Employees', revenue: 'Revenue — Paid Fees', sports: 'Sports' };
const PROFILE_TABS = new Set(['students', 'employees']);
export const personProfilePath = (academyId, kind, personId) => `/super-admin/academies/${academyId}/${kind}/${personId}`;
const openProfile = (academyId, kind, personId) => window.open(personProfilePath(academyId, kind, personId), '_blank', 'noopener,noreferrer');

export function AcademyDrilldown({ academy, tab, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setData(null);
    api.get(`/admin/academies/${academy.id}/drilldown/${tab}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ rows: [], error: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [academy.id, tab]);

  const cols = COLUMNS[tab];
  const rows = data?.rows || [];
  const linkable = PROFILE_TABS.has(tab);
  const colCount = cols.length;

  return (
    <div className="space-y-3 min-w-0 w-full" data-testid={`drilldown-${tab}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onBack} data-testid="drilldown-back-btn">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Overview
          </Button>
          <h3 className="text-sm font-semibold">{TITLES[tab]}</h3>
          {!loading && <Badge variant="secondary" className="text-xs tabular-nums" data-testid="drilldown-count">{rows.length}</Badge>}
          {linkable && !loading && rows.length > 0 && <span className="text-[11px] text-muted-foreground hidden sm:inline">Click a row to open the full profile in a new tab</span>}
        </div>
        {tab === 'revenue' && !loading && (
          <span className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="drilldown-revenue-total">
            Total {inr(data?.total_amount)}
          </span>
        )}
      </div>

      <div className="rounded-lg border overflow-hidden w-full min-w-0">
        <div className="max-h-[42vh] overflow-auto w-full">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                {cols.map((c) => (
                  <TableHead key={c.h} className={`text-xs whitespace-nowrap h-9 ${c.right ? 'text-right' : ''}`}>{c.h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>{cols.map((c) => <TableCell key={c.h} className="py-2"><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                ))
              ) : data?.error ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
                    <p className="text-sm text-muted-foreground" data-testid="drilldown-error">Couldn't load {TITLES[tab].toLowerCase().split(' ')[0]} — please try again</p>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-10">
                    <Inbox className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground" data-testid="drilldown-empty">No {TITLES[tab].toLowerCase().split(' ')[0]} recorded yet</p>
                  </TableCell>
                </TableRow>
              ) : rows.map((row, i) => (
                <TableRow
                  key={row.id || row.custom_id || row.sport || i}
                  data-testid={`drilldown-row-${i}`}
                  className={linkable ? 'cursor-pointer hover:bg-primary/5' : ''}
                  onClick={linkable && row.id ? () => openProfile(academy.id, tab, row.id) : undefined}
                >
                  {cols.map((c) => (
                    <TableCell key={c.h} className={`text-sm py-2 whitespace-nowrap ${c.right ? 'text-right tabular-nums' : ''}`}>
                      {linkable && c.h === 'Name' ? (
                        <span className="inline-flex items-center gap-1.5">
                          {c.r(row)}
                          <a
                            href={personProfilePath(academy.id, tab, row.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/70 hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Open full profile in a new tab"
                            data-testid={`drilldown-open-${i}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </span>
                      ) : c.r(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
